import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import apiMap from "../../apiMapping.json";
import "./ChapterList.css";
import FetchError from "../FetchError/FetchError";
import { createCachedGet, invalidateCachedUrl } from "../../utils/apiCache";
import { verseCountForChapter } from "../../config/gita-canon";

const cachedGet = createCachedGet(axios);

/** Always show 18 chapters; merge API data with canonical verse counts as fallback. */
function mergeEighteenChapters(apiChapters) {
  const list = Array.isArray(apiChapters) ? apiChapters : [apiChapters];
  const byNum = new Map();
  list.forEach((c) => {
    const n = c && c.chapterNumber != null ? Number(c.chapterNumber) : NaN;
    if (c && Number.isFinite(n) && n >= 1 && n <= 18) {
      byNum.set(n, { ...c, chapterNumber: n });
    }
  });
  return Array.from({ length: 18 }, (_, i) => {
    const n = i + 1;
    const fromApi = byNum.get(n);
    const fallbackVerses = verseCountForChapter(n);
    if (fromApi) {
      return {
        ...fromApi,
        verseCount: fromApi.verseCount ?? fallbackVerses,
      };
    }
    return {
      chapterNumber: n,
      chapterName: `Adhyāya ${n}`,
      verseCount: fallbackVerses,
      _placeholder: true,
    };
  });
}

function ChapterList({ language, selectedChapter, onSelectChapterNumber }) {
  const [chapters, setChapters] = useState([]);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  const listUrl = `${apiMap.hostData}${apiMap.apiData}chapterByLanguage/${language}`;

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      try {
        setError(null);
        setLoaded(false);
        const { data: chapterResponse } = await cachedGet(listUrl, { signal: controller.signal });
        setChapters(mergeEighteenChapters(chapterResponse));
        setLoaded(true);
      } catch (err) {
        if (axios.isCancel?.(err) || err.code === "ERR_CANCELED" || err.name === "CanceledError") {
          return;
        }
        setError("Could not load chapters for this language. Try another translation.");
        console.error(err);
      }
    };

    fetchData();
    return () => controller.abort();
  }, [language, listUrl, retryKey]);

  const handleRetry = useCallback(() => {
    invalidateCachedUrl(listUrl);
    setRetryKey((k) => k + 1);
  }, [listUrl]);

  const totalVersesLabel = useMemo(() => {
    return chapters.reduce((acc, c) => acc + (Number(c.verseCount) || 0), 0);
  }, [chapters]);

  if (error) {
    return <FetchError message={error} onRetry={handleRetry} />;
  }

  return loaded ? (
    <div className="chapter-list">
      <div className="chapter-list__head">
        <h2 className="chapter-list__title">Eighteen chapters</h2>
        <p className="chapter-list__meta">
          {totalVersesLabel ? <span>{totalVersesLabel} verses total</span> : null}
        </p>
      </div>
      <div className="chapter-list__grid" role="list">
        {chapters.map((chapter) => (
          <div
            className={
              "chapter-card" +
              (selectedChapter === chapter.chapterNumber ? " chapter-card--selected" : "") +
              (chapter._placeholder ? " chapter-card--muted" : "")
            }
            key={chapter.chapterNumber}
            role="listitem"
          >
            <button
              type="button"
              className="chapter-card__btn"
              aria-current={selectedChapter === chapter.chapterNumber ? "true" : undefined}
              aria-label={`Chapter ${chapter.chapterNumber}, ${chapter.chapterName}`}
              onClick={() => onSelectChapterNumber(chapter.chapterNumber)}
            >
              <span className="chapter-card__num">{chapter.chapterNumber}</span>
              <span className="chapter-card__name">{chapter.chapterName}</span>
              <span className="chapter-card__verses">
                {chapter.verseCount != null ? `${chapter.verseCount} ślokas` : "—"}
              </span>
            </button>
          </div>
        ))}
      </div>
    </div>
  ) : (
    <div className="gita-loader" aria-busy="true" aria-label="Loading chapters">
      <span className="gita-loader__ring" aria-hidden />
      <span>Loading chapters…</span>
    </div>
  );
}

export default ChapterList;
